import React from "react";
import { Checkbox } from "../../UI/Checkbox";
import { styled } from "@mui/material/styles";
import { Badge } from "../../UI/Badge";
import { TableCell as MuiTableCell } from "../../UI/TableCell";
import { TableHead } from "../../UI/TableHead";
import { TableRow } from "../../UI/TableRow";
import { usePlacementWorksheetSettingsContext } from "../../contexts/PlacementWorksheetContext";
import { COLOR_PICKLE_BLUEWOOD, COLOR_WHITE } from "../../constants/styles";

interface IPlacementWorksheetTableHeadProps {
  isAllChecked: boolean;
  isIndeterminate: boolean;
  notAssignedRowsQty?: number;
  onSelectAll: (checked: boolean) => void;
  isReadonly?: boolean;
}



// Create a sticky table cell component for vertical stickiness (all header cells)
const TableCell = styled(MuiTableCell)`
  position: sticky;
  top: 0;
  z-index: 10; /* Below filters but above content */
  background-color: ${COLOR_PICKLE_BLUEWOOD} !important; /* Use the standard table header color */
  min-width: 120px; /* Ensure enough width for resident names */
  color: ${COLOR_WHITE};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  transition: box-shadow 0.2s;
  margin-top: 0;
  padding-top: 8px;
  padding-bottom: 8px;

  /* Add a subtle shadow when scrolling for better visual separation */
  box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.1);

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

// Create a sticky table cell for the first three columns (left sticky)
const LeftStickyTableCell = styled(TableCell)`
  position: sticky;
  left: ${(props) => props.leftPosition || 0}px;
  z-index: 11; /* Higher z-index than regular header cells */
  background-color: ${COLOR_PICKLE_BLUEWOOD} !important; /* Match the header background color */
`;

// Create a sticky table cell for the last three columns (right sticky)
const RightStickyTableCell = styled(TableCell)`
  position: sticky !important;
  right: ${(props) => props.rightPosition || 0}px !important;
  z-index: 12 !important; /* Higher z-index than left sticky cells */
  white-space: normal !important; /* Prevent text from collapsing */
  overflow: visible !important; /* Ensure content is not cut off */
  box-sizing: border-box !important; /* Include padding in width calculation */
  /* width, min-width, and max-width are now controlled by the sx prop */
  background-color: ${COLOR_PICKLE_BLUEWOOD} !important; /* Match the TableHead background color */
  flex-shrink: 0 !important; /* Prevent column from shrinking */
  flex-grow: 0 !important; /* Prevent column from growing */

  /* Add a left border for visual separation when scrolled */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: rgba(255, 255, 255, 0.3);
  }
`;

export const PlacementWorksheetTableHead: React.FC<IPlacementWorksheetTableHeadProps> = ({
  isAllChecked,
  isIndeterminate,
  onSelectAll,
  notAssignedRowsQty,
  isReadonly
}) => {
  const handleHeaderCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAll(e.target.checked);
  };

  const { labels } = usePlacementWorksheetSettingsContext();

  // Calculate the number of columns for proper positioning
  // const totalColumns = labels.length + 6; // 6 fixed columns + dynamic room columns

  // Adjust right positions based on whether the actions column is shown
  // const actionsColumnWidth = isReadonly ? 0 : 120;
  const commentsRightPosition = isReadonly ? 0 : 120;
  const chooseRoomRightPosition = commentsRightPosition + 180; // 180px is internal comments width

  return (
    <TableHead>
      <TableRow>
        <LeftStickyTableCell leftPosition={0} sx={{ width: '50px', minWidth: '50px', maxWidth: '50px' }}>
          <Checkbox isChecked={isAllChecked} isIndeterminate={isIndeterminate} onChange={handleHeaderCheckboxChange} isDisabled={isReadonly}/>
        </LeftStickyTableCell>
        <LeftStickyTableCell leftPosition={50} sx={{ width: '75px', minWidth: '75px', maxWidth: '75px' }}>Unit #</LeftStickyTableCell>
        {labels.map((label) => (
          <TableCell key={label.id} sx={{ minWidth: '150px' }}>Room: {label.label}</TableCell>
        ))}
        <RightStickyTableCell rightPosition={chooseRoomRightPosition} sx={{ width: '145px !important', minWidth: '145px !important', maxWidth: '145px !important', borderLeft: '2px solid rgba(255, 255, 255, 0.3)' }}>
          Choose Room {!!notAssignedRowsQty && <Badge appearance="important">{notAssignedRowsQty}</Badge>}
        </RightStickyTableCell>
        <RightStickyTableCell rightPosition={commentsRightPosition} sx={{ minWidth: '180px', width: '180px' }}>Internal Comments</RightStickyTableCell>
        {!isReadonly && <RightStickyTableCell rightPosition={0} />}
      </TableRow>
    </TableHead>
  );
};
